import React, { useEffect, useMemo, useState, useRef } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, ShoppingBag, UserPlus, Download, Trash2, Check, X, Radio, Gift, Bell, Star, Plus, Pencil, Save, Package, Upload, Truck, FileText, Trophy, Wallet, MessageCircle, QrCode, Mail, Clock } from "lucide-react";
import { api, imageUrl, whatsappLink, downloadFile } from "../lib/api";
import { PromoManager } from "../components/PromoManager";
import { ReviewBonusSettings } from "../components/ReviewBonusSettings";
import { HomeBannerSettings } from "../components/HomeBannerSettings";
import { RewardsAdmin } from "../components/RewardsAdmin";
import { ClubAdmin } from "../components/ClubAdmin";
import { BlogAdmin } from "../components/BlogAdmin";
import { AdminWeeklyReport } from "../components/AdminWeeklyReport";
import { InvoiceRegister } from "../components/InvoiceRegister";
import { AdminListFilters, applyAdminFilters, EMPTY_FILTERS } from "../components/AdminListFilters";
import { CityLeadsAdmin } from "../components/CityLeadsAdmin";
import { ContactAdmin } from "../components/ContactAdmin";
import { AdminCleanup } from "../components/AdminCleanup";
import { NationalPaymentSettings } from "../components/NationalPaymentSettings";
import { AdminRestockBuilder } from "../components/AdminRestockBuilder";
import { FactoryStockInput, VendorStockAccordion } from "../components/AdminStockTools";
import { SeoSettingsForm, AudienceDashboard, VendorResponseTimes, StockoutsPanel } from "../components/AdminInsights";
import { PushToggle } from "../components/PushToggle";
import { CompanySettings, TestOrdersTool } from "../components/DataSecurityAdmin";
import { useAuth } from "../context/AuthContext";
import { LiveBadge } from "../components/LiveBadge";

const STATUS_LABEL = { attente_paiement: "Attente paiement", annulee: "Annulée", nouvelle: "Nouvelle", acceptee: "Acceptée", en_livraison: "En livraison", livree: "Livrée", refusee: "Refusée", a_verifier: "À vérifier" };

const playPromoChime = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [[523.25, 0], [659.25, 0.15], [783.99, 0.3], [1046.5, 0.45]].forEach(([freq, delay]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.001, ctx.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + delay + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + delay);
      osc.stop(ctx.currentTime + delay + 0.4);
    });
  } catch (e) { console.debug("Audio bloqué avant interaction:", e?.message); }
};

const promoWaLink = (e) => {
  const num = (e.whatsapp || "").replace(/\D/g, "");
  const msg = `Félicitations ${e.vendor_name} ! 🏆 Tu passes officiellement Vendeur ${e.to_tier} sur STUFF CBD. Ta marge augmente, continue comme ça, toute l'équipe est fière de toi ! 🔥`;
  return num ? `https://wa.me/${num}?text=${encodeURIComponent(msg)}` : null;
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "vendors");
  const [stats, setStats] = useState(null);
  const [promotions, setPromotions] = useState([]);
  const knownPromoIds = useRef(new Set());
  const firstPromoLoad = useRef(true);

  useEffect(() => { if (user && user.role === "admin") api.get("/admin/stats").then((r) => setStats(r.data)).catch(console.error); }, [user, tab]);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    const poll = () => api.get("/admin/tier-promotions").then((r) => {
      const events = r.data.events || [];
      const fresh = events.filter((e) => !knownPromoIds.current.has(e.id));
      events.forEach((e) => knownPromoIds.current.add(e.id));
      if (fresh.length > 0 && !firstPromoLoad.current) playPromoChime();
      firstPromoLoad.current = false;
      setPromotions(events);
    }).catch(console.error);
    poll();
    const iv = setInterval(poll, 60000);
    return () => clearInterval(iv);
  }, [user]);

  const dismissPromotion = async (id) => {
    setPromotions((ps) => ps.filter((p) => p.id !== id));
    try { await api.post(`/admin/tier-promotions/${id}/seen`); } catch (e) { console.error("tier-promotion seen:", e); }
  };

  if (user === null) return <div className="p-10 text-zinc-500">Chargement...</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-anton text-4xl uppercase text-white mb-1">Admin <span className="neon-text-magenta">STUFF</span></h1>
          <p className="text-zinc-500 text-sm">Maison mère — gestion du réseau</p>
        </div>
        <div className="flex items-center gap-2">
          {promotions.length > 0 && (
            <div data-testid="admin-promo-badge" className="relative p-3 border border-yellow-400/70" title="Promotions de statut">
              <Trophy className="h-5 w-5 text-yellow-400" />
              <span data-testid="admin-promo-badge-count" className="absolute -top-1 -right-1 bg-yellow-400 text-black text-xs font-bold h-5 min-w-5 px-1 flex items-center justify-center rounded-full animate-flicker">{promotions.length}</span>
            </div>
          )}
          <button
            data-testid="admin-bell"
            onClick={() => setTab("candidatures")}
            className="relative p-3 border border-zinc-800 hover:border-neon-magenta transition-colors"
            title="Candidatures vendeurs à valider"
          >
            <Bell className="h-5 w-5 text-neon-magenta" />
            {stats && stats.stuck_orders > 0 && (
              <span data-testid="admin-stuck-count" title="Commandes bloquées / à vérifier" className="absolute -bottom-1 -right-1 bg-orange-400 text-black text-xs font-bold h-5 min-w-5 px-1 flex items-center justify-center rounded-full">{stats.stuck_orders}</span>
            )}
            {stats && stats.pending_vendors > 0 && (
              <span data-testid="admin-bell-count" className="absolute -top-1 -right-1 bg-neon-magenta text-white text-xs font-bold h-5 min-w-5 px-1 flex items-center justify-center rounded-full animate-flicker">{stats.pending_vendors}</span>
            )}
          </button>
          <button
            data-testid="admin-contact-bell"
            onClick={() => setTab("contact")}
            className="relative p-3 border border-zinc-800 hover:border-neon-green transition-colors"
            title="Messages de contact"
          >
            <Mail className="h-5 w-5 text-neon-green" />
            {stats && stats.contact_unread > 0 && (
              <span data-testid="admin-contact-count" className="absolute -top-1 -right-1 bg-neon-green text-black text-xs font-bold h-5 min-w-5 px-1 flex items-center justify-center rounded-full">{stats.contact_unread}</span>
            )}
          </button>
        </div>
      </div>

      {promotions.map((e) => {
        const wa = promoWaLink(e);
        return (
          <div key={e.id} data-testid="admin-promo-banner" className="border border-yellow-400/70 bg-yellow-400/10 px-4 py-3 mb-6 text-sm flex flex-wrap items-center gap-3">
            <Trophy className="h-5 w-5 text-yellow-400 shrink-0" />
            <span className="text-white">
              <span className="font-anton uppercase text-yellow-400">Promotion !</span> <span className="font-bold">{e.vendor_name}</span>{e.city ? ` (${e.city})` : ""} passe <span className="font-bold text-yellow-400">Vendeur {e.to_tier}</span> 🏆 (ex-{e.from_tier})
            </span>
            <div className="ml-auto flex items-center gap-2">
              {wa && (
                <a data-testid="admin-promo-whatsapp" href={wa} target="_blank" rel="noreferrer" className="text-xs uppercase font-bold bg-yellow-400 text-black px-3 py-1.5">Féliciter sur WhatsApp</a>
              )}
              <button data-testid="admin-promo-dismiss" onClick={() => dismissPromotion(e.id)} className="p-1 text-zinc-400 hover:text-white"><X className="h-4 w-4" /></button>
            </div>
          </div>
        );
      })}

      {stats && stats.pending_vendors > 0 && (
        <div data-testid="admin-new-app-banner" className="border border-neon-magenta/50 bg-neon-magenta/10 text-neon-magenta px-4 py-3 mb-6 text-sm flex items-center gap-2">
          <UserPlus className="h-4 w-4" /> {stats.pending_vendors} boutique{stats.pending_vendors > 1 ? "s" : ""} en attente de validation.
          <button onClick={() => setTab("candidatures")} className="ml-auto underline font-bold">Valider</button>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-8 gap-3 mb-8">
          <Stat label="Vendeurs" value={stats.vendors} icon={Users} />
          <Stat label="À valider" value={stats.pending_vendors || 0} icon={UserPlus} color="text-neon-magenta" />
          <Stat label="En ligne" value={stats.vendors_live} icon={Radio} color="text-neon-green" />
          <Stat label="Commandes" value={stats.orders} icon={ShoppingBag} />
          <Stat label="Réassort" value={stats.restock || 0} icon={Truck} color="text-neon-magenta" />
          <Stat label="À vérifier" value={stats.a_verifier || 0} icon={Clock} color="text-orange-400" />
          <Stat label="Tests" value={stats.test_orders || 0} icon={Trash2} color="text-zinc-400" />
          <Stat label="Échanges" value={stats.redemptions || 0} icon={Gift} color="text-neon-magenta" />
        </div>
      )}

      <div className="mb-4"><PushToggle /></div>
      <div className="flex gap-1 sm:gap-2 border-b border-zinc-800 mb-6 overflow-x-auto no-scrollbar scroll-fade-x sm:[mask-image:none] -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap sm:overflow-visible">
        {[["vendors", "Vendeurs"], ["candidatures", "Candidatures"], ["catalog", "Catalogue"], ["stock", "Stock"], ["reassort", "Réassort"], ["orders", "Commandes"], ["invoices", "Factures"], ["earnings", "Gains"], ["report", "Bilan"], ["rewards", "Récompenses"], ["club", "Club"], ["blog", "Blog"], ["promos", "Promos"], ["referrals", "Parrainage"], ["customers", "Clients"], ["prospects", "Candidatures livreurs"], ["leads", "Leads villes"], ["contact", "Messages"], ["seo", "SEO & Audience"], ["cleanup", "Nettoyage"], ["tests", "Tests"], ["gifts", "Cadeaux"], ["redemptions", "Échanges"]].map(([k, label]) => (
          <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)} className={`shrink-0 px-4 py-3 uppercase text-sm font-bold transition-colors ${tab === k ? "text-neon-magenta border-b-2 border-neon-magenta" : "text-zinc-500 hover:text-white"}`}>{label}</button>
        ))}
      </div>

      {tab === "vendors" && <VendorsTab />}
      {tab === "tests" && <TestOrdersTool />}
      {tab === "candidatures" && <CandidaturesTab />}
      {tab === "catalog" && <CatalogTab />}
      {tab === "stock" && <AdminStockTab />}
      {tab === "reassort" && <AdminReassortTab />}
      {tab === "orders" && <OrdersTab />}
      {tab === "earnings" && <AdminEarningsTab />}
      {tab === "report" && <AdminWeeklyReport />}
      {tab === "invoices" && <InvoiceRegister />}
      {tab === "rewards" && <RewardsAdmin />}
      {tab === "club" && <ClubAdmin />}
      {tab === "blog" && <BlogAdmin />}
      {tab === "promos" && <><HomeBannerSettings /><ReviewBonusSettings /><PromoManager scope="admin" /></>}
      {tab === "referrals" && <ReferralsTab />}
      {tab === "customers" && <CustomersTab />}
      {tab === "prospects" && <ApplicationsTab />}
      {tab === "leads" && <CityLeadsAdmin />}
      {tab === "contact" && <ContactAdmin />}
      {tab === "cleanup" && <AdminCleanup />}
      {tab === "seo" && <div className="space-y-6"><AudienceDashboard /><SeoSettingsForm /></div>}
      {tab === "gifts" && <GiftsTab />}
      {tab === "redemptions" && <RedemptionsTab />}
    </div>
  );
}

function CandidaturesTab() {
  const [vendors, setVendors] = useState([]);
  const load = () => api.get("/admin/vendors").then((r) => setVendors(r.data.filter((v) => !v.is_approved))).catch(console.error);
  useEffect(() => { load(); }, []);
  const approve = async (id) => { await api.patch(`/admin/vendors/${id}`, { is_approved: true }); load(); toast.success("Boutique validée et mise en ligne"); };
  const reject = async (id) => { if (!window.confirm("Refuser et supprimer cette candidature ?")) return; await api.delete(`/admin/vendors/${id}`); load(); toast.success("Candidature refusée"); };

  if (vendors.length === 0) return <p className="text-zinc-500 py-10 text-center">Aucune candidature de boutique en attente.</p>;
  return (
    <div className="space-y-3">
      <p className="text-zinc-500 text-sm">Valide une candidature pour <span className="text-neon-green">mettre la boutique en ligne automatiquement</span>. Le vendeur n'a plus qu'à gérer son stock.</p>
      {vendors.map((v) => (
        <div key={v.id} data-testid="candidature-row" className="bg-ink-surface border border-neon-magenta/30 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <span className="font-anton text-lg text-white">{v.name}</span>
            <span className="ml-2 text-xs text-neon-magenta border border-neon-magenta px-2 py-0.5 uppercase">En attente</span>
            <p className="text-zinc-400 text-sm mt-1">{v.email} — {v.city} — 📞 {v.phone}</p>
            <p className="text-zinc-600 text-xs">WA: {v.whatsapp || "—"} · TG: {v.telegram || "—"}</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="validate-candidature" onClick={() => approve(v.id)} className="flex items-center gap-1 text-xs uppercase font-bold bg-neon-green text-black px-4 py-2 glow-green"><Check className="h-4 w-4" /> Valider la boutique</button>
            <button data-testid="reject-candidature" onClick={() => reject(v.id)} className="p-2 text-zinc-400 hover:text-red-500 border border-zinc-700"><X className="h-4 w-4" /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

function AdminStockTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [ft, setFt] = useState(20);
  const [vt, setVt] = useState(5);
  const loadAlerts = () => api.get("/admin/stock-alerts").then((r) => { setAlerts(r.data); setFt(r.data.factory_threshold); setVt(r.data.vendor_threshold); }).catch(console.error);
  useEffect(() => { api.get("/admin/stock").then((r) => setItems(r.data)).catch(console.error); loadAlerts(); }, []);
  const saveThresholds = async () => {
    await api.put("/admin/stock-alerts", { factory_threshold: parseInt(ft) || 0, vendor_threshold: parseInt(vt) || 0 });
    loadAlerts();
    toast.success("Seuils d'alerte enregistrés");
  };

  const byCat = useMemo(() => items.reduce((acc, i) => { (acc[i.category] ||= []).push(i); return acc; }, {}), [items]);
  const cats = Object.keys(byCat);
  if (items.length === 0) return <p className="text-zinc-500 py-10 text-center">Catalogue vide.</p>;
  return (
    <div className="space-y-6">
      <div className="bg-ink-surface border border-zinc-800 p-4" data-testid="stock-alerts-panel">
        <div className="flex items-end gap-3 flex-wrap mb-3">
          <h3 className="font-anton text-lg uppercase text-white">⚠️ Alertes stock</h3>
          <label className="text-zinc-400 text-xs uppercase">Seuil usine
            <input data-testid="alert-factory-threshold" type="number" min="0" value={ft} onChange={(e) => setFt(e.target.value)} className="mt-1 block w-24 bg-ink border border-zinc-700 text-white px-3 py-1.5 focus:outline-none focus:border-neon-magenta" />
          </label>
          <label className="text-zinc-400 text-xs uppercase">Seuil boutique
            <input data-testid="alert-vendor-threshold" type="number" min="0" value={vt} onChange={(e) => setVt(e.target.value)} className="mt-1 block w-24 bg-ink border border-zinc-700 text-white px-3 py-1.5 focus:outline-none focus:border-neon-magenta" />
          </label>
          <button data-testid="alert-save-btn" onClick={saveThresholds} className="text-xs uppercase font-bold border border-neon-green text-neon-green px-3 py-2 hover:bg-neon-green hover:text-black transition-colors">Enregistrer</button>
        </div>
        {alerts && (
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-red-400 text-xs uppercase font-bold mb-1.5">🏭 Stock usine ≤ {alerts.factory_threshold} ({alerts.factory_alerts.length})</h4>
              {alerts.factory_alerts.length === 0 ? <p className="text-zinc-600 text-xs">Aucune alerte usine ✓</p> : (
                <div className="space-y-1 max-h-48 overflow-y-auto" data-testid="factory-alerts-list">
                  {alerts.factory_alerts.map((a) => (
                    <div key={a.id} className="flex justify-between text-sm border-b border-zinc-900 py-1">
                      <span className="text-zinc-300">{a.name}</span>
                      <span className={`font-anton ${a.factory_stock === 0 ? "text-red-500" : "text-orange-400"}`}>{a.factory_stock}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <h4 className="text-orange-400 text-xs uppercase font-bold mb-1.5">🏪 Stock boutique ≤ {alerts.vendor_threshold} ({alerts.vendor_alerts.length})</h4>
              {alerts.vendor_alerts.length === 0 ? <p className="text-zinc-600 text-xs">Aucune alerte boutique ✓</p> : (
                <div className="space-y-1 max-h-48 overflow-y-auto" data-testid="vendor-alerts-list">
                  {alerts.vendor_alerts.map((a, i) => (
                    <div key={`${a.vendor_name}-${a.product_name}`} className="flex justify-between gap-2 text-sm border-b border-zinc-900 py-1">
                      <span className="text-zinc-300 truncate">{a.vendor_name} <span className="text-zinc-600">({a.city})</span> — {a.product_name}</span>
                      <span className={`font-anton shrink-0 ${a.quantity === 0 ? "text-red-500" : "text-orange-400"}`}>{a.quantity}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        <p className="text-zinc-600 text-[11px] mt-2">Prépare un réassort dans l'onglet Réassort : le produit affichera « De retour en stock dans quelques jours » sur la boutique concernée.</p>
      </div>

      <StockoutsPanel />

      <VendorStockAccordion />

      <p className="text-zinc-500 text-xs">🏭 Modifie le <span className="text-white font-bold">stock usine</span> directement sur chaque produit puis clique sur 💾 — la boutique nationale (colis 48h) est mise à jour instantanément.</p>

      {cats.map((cat) => (
        <div key={cat}>
          <h3 className="font-anton text-lg uppercase text-neon-magenta mb-2">{cat}</h3>
          <div className="space-y-2">
            {byCat[cat].map((p) => (
              <div key={p.id} data-testid="admin-stock-row" className="bg-ink-surface border border-zinc-800">
                <div className="w-full flex items-center gap-3 p-3">
                  <button onClick={() => setOpen(open === p.id ? null : p.id)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                    <img src={imageUrl(p.image_url) || "https://customer-assets.emergentagent.com/job_store-hub-157/artifacts/yyc18991_IMG_4514.jpeg"} alt={p.name} className="h-12 w-12 object-cover" />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white font-semibold truncate">{p.name}</h4>
                      <span className="text-xs text-zinc-400">Stock usine: <span className="text-neon-green font-bold" data-testid="stock-factory-value">{p.factory_stock}</span> · Stock boutiques: <span className="text-neon-magenta font-bold">{p.total_vendor_stock}</span></span>
                    </div>
                  </button>
                  <FactoryStockInput product={p} onSaved={(id, n) => setItems((arr) => arr.map((x) => (x.id === id ? { ...x, factory_stock: n } : x)))} />
                </div>
                {open === p.id && (
                  <div className="border-t border-zinc-800 px-4 py-2">
                    {p.per_vendor.length === 0 ? <p className="text-zinc-600 text-xs py-1">Aucun vendeur n'a ce produit.</p> : p.per_vendor.map((v, idx) => (
                      <div key={v.vendor_name} className="flex justify-between text-sm py-1 border-b border-zinc-900 last:border-0">
                        <span className="text-zinc-400">{v.vendor_name} <span className="text-zinc-600">({v.city})</span></span>
                        <span className="text-white font-semibold">{v.quantity}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function AdminRestockRow({ o, onChanged }) {
  const [q, setQ] = useState(() => Object.fromEntries(o.items.map((i) => [i.catalog_id, i.quantity || ""])));
  const [busy, setBusy] = useState(false);
  const STATUS = { "demandée": ["Demandée (boutique)", "text-yellow-400"], "préparée": ["En préparation", "text-neon-magenta"], "livrée": ["Livré ✓", "text-neon-green"] };
  const isRequest = o.status === "demandée";
  const total = o.items.reduce((s, i) => s + (parseInt(isRequest ? q[i.catalog_id] : i.quantity) || 0), 0);

  const setStatus = async (status) => {
    setBusy(true);
    try {
      const body = { status };
      if (isRequest) body.items = o.items.map((i) => ({ catalog_id: i.catalog_id, quantity: parseInt(q[i.catalog_id]) || 0 }));
      await api.patch(`/admin/restock/${o.id}`, body);
      toast.success(status === "livrée" ? "Livré — stock boutique mis à jour" : "En préparation — stock usine soustrait, bon de livraison prêt");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!window.confirm("Annuler ce réassort ?" + (o.factory_deducted ? " Le stock usine sera restauré." : ""))) return;
    await api.delete(`/admin/restock/${o.id}`); onChanged(); toast.success("Réassort annulé");
  };
  const slip = async () => {
    try { await downloadFile(`/restock/${o.id}/slip.pdf`, `BL-${o.ref}.pdf`); toast.success("Bon de livraison téléchargé 📄"); }
    catch { toast.error("Impossible de générer le bon de livraison"); }
  };

  return (
    <div data-testid="admin-restock-row" className={`bg-ink-surface border p-4 ${isRequest ? "border-yellow-400/50" : "border-zinc-800"}`}>
      <div className="flex justify-between items-start gap-3 flex-wrap">
        <div>
          <span className="font-mono text-neon-green">#{o.ref}</span>
          <span className="ml-3 font-anton text-lg text-white">{o.vendor_name}</span>
          <span className="ml-2 text-xs text-zinc-500">{o.city}</span>
          <span className={`ml-2 text-xs uppercase font-bold ${(STATUS[o.status] || ["", "text-zinc-400"])[1]}`}>{(STATUS[o.status] || [o.status])[0]}</span>
          {o.created_by === "admin" && <span className="ml-2 text-[10px] uppercase text-zinc-600 border border-zinc-800 px-1.5 py-0.5">Maison mère</span>}
          {o.factory_deducted && o.status !== "livrée" && <span className="ml-2 text-[10px] uppercase text-neon-magenta border border-neon-magenta/40 px-1.5 py-0.5">Stock usine soustrait</span>}
          {o.delivered_at && <span className="ml-2 text-xs text-zinc-600">livré le {new Date(o.delivered_at).toLocaleDateString("fr-FR")}</span>}
        </div>
        <div className="flex gap-2 flex-wrap">
          {!isRequest && (
            <button data-testid="restock-slip-btn" onClick={slip} className="inline-flex items-center gap-1 text-xs uppercase font-bold border border-zinc-700 text-zinc-300 px-3 py-2 hover:border-neon-green hover:text-neon-green"><FileText className="h-3.5 w-3.5" /> Bon de livraison PDF</button>
          )}
          {isRequest && (
            <button data-testid="restock-prepared" disabled={busy || total === 0} onClick={() => setStatus("préparée")} className="text-xs uppercase font-bold border border-neon-magenta text-neon-magenta px-3 py-2 hover:bg-neon-magenta hover:text-white disabled:opacity-40">Préparer ({total} u.) — soustraire usine</button>
          )}
          {o.status === "préparée" && (
            <button data-testid="restock-delivered" disabled={busy} onClick={() => setStatus("livrée")} className="text-xs uppercase font-bold bg-neon-green text-black px-3 py-2">Marquer livré</button>
          )}
          {o.status !== "livrée" && (
            <button data-testid="restock-cancel" onClick={cancel} className="text-xs uppercase font-bold border border-zinc-700 text-zinc-400 px-3 py-2 hover:border-red-500 hover:text-red-500">Annuler</button>
          )}
        </div>
      </div>
      {isRequest && <p className="text-yellow-300/80 text-xs mt-2">La boutique a indiqué les produits — fixe la quantité de chaque ligne (0 = ne pas envoyer).</p>}
      <table className="w-full text-sm mt-3">
        <tbody>
          {o.items.map((i) => (
            <tr key={i.catalog_id} className="border-b border-zinc-900 text-zinc-300">
              <td className="py-1">{i.name}</td>
              <td className="py-1 text-zinc-600">{i.category}</td>
              <td className="py-1 text-right font-anton text-neon-green">
                {isRequest ? (
                  <input data-testid="restock-request-qty" type="number" min="0" inputMode="numeric" placeholder="0" value={q[i.catalog_id]} onChange={(e) => setQ((m) => ({ ...m, [i.catalog_id]: e.target.value }))} className="w-20 bg-ink border border-zinc-700 text-white px-2 py-1.5 text-center font-outfit focus:outline-none focus:border-neon-magenta" />
                ) : `x${i.quantity}`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AdminReassortTab() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("actifs");
  const load = () => api.get("/admin/restock").then((r) => setOrders(r.data)).catch(console.error);
  useEffect(() => { load(); }, []);
  const pending = orders.filter((o) => o.status === "demandée").length;
  const visible = orders.filter((o) => filter === "tous" || (filter === "actifs" ? o.status !== "livrée" : o.status === filter));

  return (
    <div className="space-y-6">
      {pending > 0 && (
        <div data-testid="restock-pending-banner" className="flex items-center gap-3 border border-yellow-400/60 bg-yellow-400/10 text-yellow-300 px-4 py-3 text-sm font-bold">
          <Bell className="h-4 w-4 animate-pulse" /> {pending} demande{pending > 1 ? "s" : ""} de réassort en attente des boutiques — fixe les quantités puis prépare
          <button onClick={() => setFilter("demandée")} className="ml-auto underline text-xs uppercase">Voir</button>
        </div>
      )}
      <AdminRestockBuilder onCreated={load} />

      <div className="flex flex-wrap items-center gap-2">
        <p className="text-zinc-500 text-sm mr-auto">Historique des réassorts (demandes boutiques + préparations maison mère).</p>
        {[["actifs", "En cours"], ["demandée", "Demandés"], ["livrée", "Livrés"], ["tous", "Tous"]].map(([k, l]) => (
          <button key={k} data-testid={`restock-filter-${k}`} onClick={() => setFilter(k)} className={`text-xs uppercase font-bold px-3 py-1.5 border transition-colors ${filter === k ? "bg-neon-green text-black border-neon-green" : "border-zinc-700 text-zinc-400 hover:text-white"}`}>{l}</button>
        ))}
      </div>
      {visible.length === 0 ? <p className="text-zinc-500 py-6 text-center">Aucun réassort dans cette vue.</p> : (
        <div className="space-y-3">
          {visible.map((o) => <AdminRestockRow key={o.id} o={o} onChanged={load} />)}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon, color = "text-white" }) {
  return (
    <div className="bg-ink-surface border border-zinc-800 p-4">
      <Icon className={`h-5 w-5 ${color} mb-2`} />
      <div className={`font-anton text-3xl ${color}`}>{value}</div>
      <div className="text-zinc-500 text-xs uppercase">{label}</div>
    </div>
  );
}

function VendorsTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(console.error); }, []);
  const [vendors, setVendors] = useState([]);
  const load = () => api.get("/admin/vendors").then((r) => setVendors(r.data)).catch(console.error);
  useEffect(() => { load(); }, []);

  const approve = async (id, val) => {
    const { data } = await api.patch(`/admin/vendors/${id}`, { is_approved: val });
    load();
    if (val && data.initial_stock?.length) toast.success(`Vendeur validé — dotation : ${data.initial_stock.length} produits x10 (déduits du stock usine)`);
    else toast.success(val ? "Vendeur validé" : "Vendeur suspendu");
  };
  const grantStock = async (id) => {
    try {
      const { data } = await api.post(`/admin/vendors/${id}/grant-stock`);
      toast.success(`Dotation versée : ${data.initial_stock.length} produits x10 (déduits du stock usine)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const del = async (id) => { if (!window.confirm("Supprimer ce vendeur et ses produits ?")) return; await api.delete(`/admin/vendors/${id}`); load(); toast.success("Supprimé"); };
  const toggleBoost = async (v) => {
    try {
      await api.patch(`/admin/vendors/${v.id}`, { force_live: !v.force_live });
      load();
      toast.success(!v.force_live ? `${v.name} affiché EN LIGNE en continu ⚡` : `${v.name} repasse en statut réel`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const resetPwd = async (v) => {
    const pwd = window.prompt(`Nouveau mot de passe pour ${v.name} (${v.email}) — 6 caractères minimum :`);
    if (pwd === null) return;
    if (pwd.length < 6) { toast.error("6 caractères minimum"); return; }
    try {
      await api.post(`/admin/vendors/${v.id}/reset-password`, { new_password: pwd });
      toast.success(`Mot de passe réinitialisé pour ${v.email} — communique-le au vendeur`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (vendors.length === 0) return <p className="text-zinc-500 py-10 text-center">Aucun vendeur inscrit.</p>;
  return (
    <div className="space-y-3">
      <CompanySettings />
      {stats?.contract_blocked > 0 && <p data-testid="contract-blocked-alert" className="border border-orange-400/60 bg-orange-400/5 text-orange-300 px-4 py-2 text-sm">⚠ {stats.contract_blocked} vendeur(s) ne peuvent pas signer le contrat : SIRET, adresse ou téléphone manquant dans leur profil.</p>}
      <VendorResponseTimes />
      {vendors.map((v) => (
        <div key={v.id} data-testid="admin-vendor-row" className="bg-ink-surface border border-zinc-800 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-anton text-lg text-white">{v.name}</span>
              <LiveBadge live={v.is_live || v.force_live} />
              {v.force_live && <span data-testid="boost-badge" className="text-xs text-black bg-neon-green px-2 py-0.5 uppercase font-bold">⚡ Boost actif</span>}
              {v.is_approved ? <span className="text-xs text-neon-green border border-neon-green px-2 py-0.5 uppercase">Validé</span> : <span className="text-xs text-neon-magenta border border-neon-magenta px-2 py-0.5 uppercase">En attente</span>}
              {!v.is_national && (v.contract_signed_at ? (
                <span data-testid="contract-signed-badge" className="text-xs text-neon-green border border-neon-green/50 px-2 py-0.5 uppercase" title={`Signé le ${new Date(v.contract_signed_at).toLocaleDateString("fr-FR")}`}>📝 Contrat signé ✓ {new Date(v.contract_signed_at).toLocaleDateString("fr-FR")}</span>
              ) : (
                <span data-testid="contract-pending-badge" className="text-xs text-orange-400 border border-orange-400/50 px-2 py-0.5 uppercase">📝 Contrat non signé</span>
              ))}
            </div>
            <p className="text-zinc-500 text-sm mt-1">{v.email} — {v.city} — 📞 {v.phone}</p>
            <p className="text-zinc-600 text-xs">{v.product_count} produits · {v.order_count} commandes · <span data-testid="vendor-qr-scans" className="text-neon-green">📱 {v.qr_scans || 0} scans QR</span> · WA: {v.whatsapp || "—"} · Snap: {v.snapchat || "—"}</p>
          </div>
          <div className="flex gap-2">
            {v.is_approved && !v.is_national && (
              <button data-testid="boost-live-btn" onClick={() => toggleBoost(v)} title="Afficher ce shop EN LIGNE en continu sur le site (statut visuel)" className={`flex items-center gap-1 text-xs uppercase font-bold px-3 py-2 border transition-colors ${v.force_live ? "bg-neon-green text-black border-neon-green" : "border-neon-green/60 text-neon-green hover:bg-neon-green hover:text-black"}`}>⚡ {v.force_live ? "Boost ON" : "Boost EN LIGNE"}</button>
            )}
            {v.is_approved && !v.initial_stock_granted && (
              <button data-testid="grant-stock-btn" onClick={() => grantStock(v.id)} className="flex items-center gap-1 text-xs uppercase font-bold border border-neon-green text-neon-green px-3 py-2 hover:bg-neon-green hover:text-black transition-colors">📦 Doter en stock (10/produit)</button>
            )}
            {v.is_approved ? (
              <button data-testid="suspend-vendor" onClick={() => approve(v.id, false)} className="flex items-center gap-1 text-xs uppercase font-bold border border-zinc-700 text-zinc-300 px-3 py-2 hover:border-neon-magenta hover:text-neon-magenta"><X className="h-4 w-4" /> Suspendre</button>
            ) : (
              <button data-testid="approve-vendor" onClick={() => approve(v.id, true)} className="flex items-center gap-1 text-xs uppercase font-bold bg-neon-green text-black px-3 py-2"><Check className="h-4 w-4" /> Valider</button>
            )}
            <button data-testid="reset-vendor-password" title="Réinitialiser le mot de passe" onClick={() => resetPwd(v)} className="flex items-center gap-1 text-xs uppercase font-bold border border-zinc-700 text-zinc-300 px-3 py-2 hover:border-yellow-400 hover:text-yellow-400 transition-colors">🔑 Mot de passe</button>
            <button data-testid="admin-vendor-qr-btn" title="Télécharger le QR code imprimable de la boutique" onClick={async () => {
              try {
                const res = await api.get(`/vendors/${v.id}/qr-card`, { responseType: "blob" });
                const url = URL.createObjectURL(new Blob([res.data], { type: "image/png" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = `qr-boutique-${(v.city || v.name || "stuff").toLowerCase().replace(/\s+/g, "-")}.png`;
                a.click();
                setTimeout(() => URL.revokeObjectURL(url), 15000);
                toast.success(`QR de ${v.name} téléchargé 📱`);
              } catch { toast.error("Impossible de générer le QR code"); }
            }} className="flex items-center gap-1 text-xs uppercase font-bold border border-zinc-700 text-zinc-300 px-3 py-2 hover:border-neon-green hover:text-neon-green transition-colors"><QrCode className="h-3.5 w-3.5" /> QR</button>
            <button data-testid="delete-vendor" onClick={() => del(v.id)} className="p-2 text-zinc-400 hover:text-red-500 border border-zinc-700"><Trash2 className="h-4 w-4" /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

function ReferralsTab() {
  const [data, setData] = useState({ leaderboard: [], tiers: [] });
  useEffect(() => { api.get("/admin/referrals").then((r) => setData(r.data)).catch(console.error); }, []);
  const medal = ["🥇", "🥈", "🥉"];
  return (
    <div>
      <div className="border border-zinc-800 bg-ink-surface p-4 mb-6 text-sm text-zinc-400 flex items-center gap-2">
        <Trophy className="h-5 w-5 text-neon-green" />
        Classement des parrains. Paliers : {data.tiers.map((t, i) => <span key={t.min} className="text-neon-green font-semibold ml-1">{t.min}+ filleuls → {t.rate_pct}%{i < data.tiers.length - 1 ? " ·" : ""}</span>)}
      </div>
      {data.leaderboard.length === 0 ? (
        <p className="text-zinc-500 py-10 text-center">Aucun parrainage actif pour l'instant.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-zinc-500 uppercase text-xs border-b border-zinc-800">
              <th className="text-left py-2 px-2">#</th><th className="text-left px-2">Parrain</th><th className="text-left px-2">Ville</th><th className="text-left px-2">Code</th><th className="text-right px-2">Filleuls</th><th className="text-right px-2">Taux</th><th className="text-right px-2">Gains parrainage</th>
            </tr></thead>
            <tbody>
              {data.leaderboard.map((r, i) => (
                <tr key={r.vendor_id} data-testid="referral-leaderboard-row" className="border-b border-zinc-900 text-zinc-300">
                  <td className="py-2 px-2">{medal[i] || i + 1}</td>
                  <td className="px-2 font-semibold text-white">{r.name}</td>
                  <td className="px-2">{r.city}</td>
                  <td className="px-2 font-mono text-neon-green text-xs">{r.referral_code}</td>
                  <td className="px-2 text-right font-anton">{r.referred_count}</td>
                  <td className="px-2 text-right text-neon-magenta font-bold">{r.rate_pct}%</td>
                  <td className="px-2 text-right font-anton text-neon-green">{Number(r.referral_earnings).toFixed(2)}€</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AdminEarningsTab() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/admin/earnings").then((r) => { setData(r.data); if (r.data.weeks[0]) setOpen(r.data.weeks[0].week_start); }).catch(console.error); }, []);
  if (!data) return <p className="text-zinc-500 py-10 text-center">Chargement…</p>;
  const g = data.grand_totals;
  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/earnings/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gains-stuff-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 15000);
      toast.success("Export CSV téléchargé 📊");
    } catch { toast.error("Erreur lors de l'export"); }
  };
  const Card = ({ label, value, color }) => (
    <div className="bg-ink-surface border border-zinc-800 p-4">
      <div className={`font-anton text-2xl ${color}`}>{Number(value).toFixed(2)}€</div>
      <div className="text-zinc-500 text-xs uppercase">{label}</div>
    </div>
  );
  return (
    <div>
      <div className="border border-zinc-800 bg-ink-surface p-4 mb-6 text-sm text-zinc-400 flex items-center gap-2 flex-wrap">
        <Wallet className="h-5 w-5 text-neon-green" />
        Récap par semaine et par vendeur (commandes <span className="text-neon-green font-semibold">livrées</span>). Ventes TTC = Gain vendeur + TVA (20%) + Gain usine. Semaine : lundi 09h00 → lundi 08h55.
        <button data-testid="earnings-export-csv" onClick={exportCsv} className="ml-auto flex items-center gap-1.5 text-xs uppercase font-bold border border-neon-green text-neon-green px-3 py-2 hover:bg-neon-green hover:text-black transition-colors"><Download className="h-4 w-4" /> Export CSV (Excel)</button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        <Card label="Ventes totales TTC" value={g.subtotal} color="text-white" />
        <Card label="Gain vendeurs" value={g.vendor_gain} color="text-neon-green" />
        <Card label="TVA (20%)" value={g.tva_gain} color="text-amber-400" />
        <Card label="Gain usine (maison mère)" value={g.factory_gain} color="text-neon-magenta" />
      </div>

      {data.weeks.length === 0 ? <p className="text-zinc-500 py-6 text-center">Aucune vente livrée pour l'instant.</p> : (
        <div className="space-y-3">
          {data.weeks.map((w) => (
            <div key={w.week_start} data-testid="admin-earnings-week" className="bg-ink-surface border border-zinc-800">
              <button onClick={() => setOpen(open === w.week_start ? null : w.week_start)} className="w-full flex items-center justify-between p-4 text-left">
                <div>
                  <div className="text-white font-semibold text-sm">{w.label}</div>
                  <div className="text-zinc-500 text-xs">{w.totals.orders_count} commande{w.totals.orders_count > 1 ? "s" : ""} · {w.vendors.length} vendeur{w.vendors.length > 1 ? "s" : ""}</div>
                </div>
                <div className="text-right">
                  <div className="font-anton text-lg text-neon-green">{w.totals.subtotal.toFixed(2)}€</div>
                  <div className="text-zinc-500 text-xs">usine {w.totals.factory_gain.toFixed(2)}€ · tva {w.totals.tva_gain.toFixed(2)}€</div>
                </div>
              </button>
              {open === w.week_start && (
                <div className="border-t border-zinc-800 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-zinc-500 uppercase text-xs border-b border-zinc-800">
                      <th className="text-left py-2 px-3">Vendeur</th><th className="text-left px-2">Ville</th><th className="text-right px-2">Cmd</th><th className="text-right px-2">Ventes</th><th className="text-right px-2">Gain vendeur</th><th className="text-right px-2">TVA</th><th className="text-right px-3">Gain usine</th>
                    </tr></thead>
                    <tbody>
                      {w.vendors.map((v) => (
                        <tr key={v.vendor_id} data-testid="admin-earnings-vendor-row" className="border-b border-zinc-900 text-zinc-300">
                          <td className="py-2 px-3 font-semibold text-white">{v.vendor_name}</td>
                          <td className="px-2 text-zinc-500">{v.city}</td>
                          <td className="px-2 text-right">{v.orders_count}</td>
                          <td className="px-2 text-right">{v.subtotal.toFixed(2)}€</td>
                          <td className="px-2 text-right text-neon-green font-semibold">{v.vendor_gain.toFixed(2)}€</td>
                          <td className="px-2 text-right text-amber-400">{v.tva_gain.toFixed(2)}€</td>
                          <td className="px-3 text-right text-neon-magenta font-semibold">{v.factory_gain.toFixed(2)}€</td>
                        </tr>
                      ))}
                      <tr className="text-white font-bold border-t border-zinc-700">
                        <td className="py-2 px-3" colSpan={3}>TOTAL SEMAINE</td>
                        <td className="px-2 text-right">{w.totals.subtotal.toFixed(2)}€</td>
                        <td className="px-2 text-right text-neon-green">{w.totals.vendor_gain.toFixed(2)}€</td>
                        <td className="px-2 text-right text-amber-400">{w.totals.tva_gain.toFixed(2)}€</td>
                        <td className="px-3 text-right text-neon-magenta">{w.totals.factory_gain.toFixed(2)}€</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <AdminInvoicesSection />
    </div>
  );
}

function AdminInvoicesSection() {
  const [invoices, setInvoices] = useState([]);
  useEffect(() => { api.get("/admin/vendor-invoices").then((r) => setInvoices(r.data)).catch(console.error); }, []);
  const download = async (inv) => {
    try {
      const res = await api.get(`/vendor-invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.number}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 15000);
      toast.success("Facture PDF téléchargée 🧾");
    } catch { toast.error("Impossible de générer la facture"); }
  };
  const isOverdue = (inv) => !inv.paid && Date.now() > new Date(inv.week_start).getTime() + 12 * 86400000;
  const waInvoiceLink = (inv) => whatsappLink(inv.vendor_whatsapp, isOverdue(inv)
    ? `Salut ${inv.vendor_name} ! 👋 Petit rappel amical : la facture STUFF CBD ${inv.number} (${inv.label}) est toujours en attente de reversement.\n\n💸 Montant à reverser à la maison mère : ${inv.totals.total_due.toFixed(2)}€\n\nMerci de régulariser dès que possible 🙏 Si le virement est déjà parti, ignore ce message. Bonne journée ! 🌿`
    : `Salut ${inv.vendor_name} ! 🧾 Ta facture STUFF CBD ${inv.number} (${inv.label}) est dispo dans ton espace vendeur → onglet Gains.\n\n💸 Montant à reverser à la maison mère : ${inv.totals.total_due.toFixed(2)}€\n✅ Tu conserves : ${inv.totals.total_kept.toFixed(2)}€\n\nMerci de faire le virement avant vendredi. Bonne semaine ! 🔥`);
  const togglePaid = async (inv) => {
    try {
      const res = await api.patch(`/admin/vendor-invoices/${inv.id}/payment`, { paid: !inv.paid });
      setInvoices((prev) => prev.map((i) => (i.id === inv.id ? { ...i, paid: res.data.paid, paid_at: res.data.paid_at } : i)));
      toast.success(res.data.paid ? "Facture marquée payée ✅" : "Facture repassée en attente");
    } catch { toast.error("Impossible de mettre à jour le statut"); }
  };
  return (
    <div className="mt-10">
      <h3 className="font-anton text-lg uppercase text-white mb-1 flex items-center gap-2"><FileText className="h-5 w-5 text-neon-magenta" /> Factures hebdomadaires vendeurs</h3>
      <p className="text-zinc-500 text-xs mb-4">Générées automatiquement chaque lundi : montant (prix usine + TVA) que chaque vendeur doit reverser à la maison mère.</p>
      {invoices.length === 0 ? <p className="text-zinc-600 text-sm">Aucune facture pour l'instant.</p> : (
        <div className="overflow-x-auto border border-zinc-800">
          <table className="w-full text-sm">
            <thead><tr className="text-zinc-500 uppercase text-xs border-b border-zinc-800 bg-ink-surface">
              <th className="text-left py-2 px-3">N° Facture</th><th className="text-left px-2">Vendeur</th><th className="text-left px-2">Semaine</th><th className="text-right px-2">À reverser</th><th className="text-right px-2">Conservé vendeur</th><th className="text-left px-2">Reversement</th><th className="text-right px-3">Actions</th>
            </tr></thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} data-testid="admin-invoice-row" className="border-b border-zinc-900 text-zinc-300">
                  <td className="py-2 px-3 font-mono text-xs text-white">{inv.number}</td>
                  <td className="px-2 font-semibold text-white">{inv.vendor_name} <span className="text-zinc-500 font-normal">({inv.city})</span></td>
                  <td className="px-2 text-zinc-500 text-xs">{inv.label}</td>
                  <td className="px-2 text-right text-neon-magenta font-anton">{inv.totals.total_due.toFixed(2)}€</td>
                  <td className="px-2 text-right text-neon-green">{inv.totals.total_kept.toFixed(2)}€</td>
                  <td className="px-2 whitespace-nowrap">
                    {inv.paid ? (
                      <span data-testid="invoice-status-paid" title={inv.paid_at ? `Payée le ${new Date(inv.paid_at).toLocaleDateString("fr-FR")}` : ""} className="inline-flex items-center gap-1 text-xs font-bold uppercase text-neon-green border border-neon-green/60 px-2 py-0.5"><Check className="h-3 w-3" /> Payé</span>
                    ) : isOverdue(inv) ? (
                      <span data-testid="invoice-status-overdue" title="Reversement en retard (échéance vendredi dépassée)" className="inline-flex items-center gap-1 text-xs font-bold uppercase text-red-400 border border-red-400/60 px-2 py-0.5">⚠ En retard</span>
                    ) : (
                      <span data-testid="invoice-status-pending" className="inline-flex items-center gap-1 text-xs font-bold uppercase text-amber-400 border border-amber-400/60 px-2 py-0.5">En attente</span>
                    )}
                    <button data-testid="invoice-toggle-paid-btn" onClick={() => togglePaid(inv)} title={inv.paid ? "Repasser en attente" : "Marquer comme payée"} className="ml-1.5 text-xs text-zinc-400 border border-zinc-700 px-2 py-0.5 hover:border-neon-green hover:text-neon-green transition-colors">{inv.paid ? "↩" : "✓ Marquer payé"}</button>
                  </td>
                  <td className="px-3 text-right whitespace-nowrap">
                    <button data-testid="admin-vendor-invoice-pdf-btn" onClick={() => download(inv)} className="inline-flex items-center gap-1 text-xs text-zinc-300 border border-zinc-700 px-2 py-1 hover:border-neon-magenta hover:text-neon-magenta transition-colors"><Download className="h-3 w-3" /> PDF</button>
                    {waInvoiceLink(inv) ? (
                      <a data-testid="admin-invoice-whatsapp-btn" href={waInvoiceLink(inv)} target="_blank" rel="noreferrer" title={isOverdue(inv) ? "Relancer le vendeur (facture en retard)" : "Envoyer le rappel de facture sur WhatsApp"} className={`ml-1.5 inline-flex items-center gap-1 text-xs px-2 py-1 border transition-colors ${isOverdue(inv) ? "text-red-400 border-red-400/60 hover:bg-red-400 hover:text-black" : "text-neon-green border-neon-green/60 hover:bg-neon-green hover:text-black"}`}><MessageCircle className="h-3 w-3" /> {isOverdue(inv) ? "Relancer" : "WhatsApp"}</a>
                    ) : (
                      <span data-testid="admin-invoice-no-whatsapp" title="Aucun numéro WhatsApp renseigné pour ce vendeur" className="ml-1.5 inline-flex items-center gap-1 text-xs text-zinc-600 border border-zinc-800 px-2 py-1 cursor-not-allowed"><MessageCircle className="h-3 w-3" /> WhatsApp</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data)).catch(console.error);
  useEffect(() => { load(); }, []);
  const paymentReceived = async (o) => {
    if (!window.confirm(`Confirmer la réception du virement de ${Number(o.total).toFixed(2)}€ pour #${o.ref} ? La commande passe en « Acceptée » et l'expédition est déclenchée.`)) return;
    try { await api.patch(`/admin/orders/${o.id}/payment-received`); toast.success("Paiement reçu — commande acceptée ✓"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const hoursSince = (iso) => (Date.now() - new Date(iso).getTime()) / 36e5;
  const payLabel = (pm) => { const l = (pm || "").toLowerCase(); return l.includes("virement") ? "🏦 Virement" : l.includes("carte") ? "💳 Carte" : "💶 Espèces"; };
  const invoice = async (o) => {
    try {
      const res = await api.get(`/admin/orders/${o.id}/invoice`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 15000);
    } catch { toast.error("Impossible de générer le reçu"); }
  };
  const decide = async (o, status) => {
    const label = status === "livree" ? "LIVRÉE (facture + points + stock conservé)" : "ANNULÉE (stock remis au vendeur)";
    if (!window.confirm(`Commande #${o.ref} — ${o.vendor_name} : marquer ${label} ?`)) return;
    try { await api.patch(`/admin/orders/${o.id}/status`, { status }); toast.success(status === "livree" ? "Commande marquée livrée ✓" : "Commande annulée"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const toVerify = orders.filter((o) => o.status === "a_verifier");
  const stuck = orders.filter((o) => o.status !== "a_verifier" && (o.stuck_hours || 0) > 0);
  const shown = applyAdminFilters(orders, filters, "created_at");
  const shownTotal = shown.reduce((s, o) => s + (Number(o.total) || 0), 0);
  if (orders.length === 0) return <p className="text-zinc-500 py-10 text-center">Aucune commande.</p>;
  return (
    <div>
      {(toVerify.length > 0 || stuck.length > 0) && (
        <div data-testid="admin-verify-block" className="border border-orange-400/60 bg-orange-400/5 p-4 mb-6">
          <h3 className="font-anton text-lg uppercase text-orange-300 mb-1">⏱️ Commandes à vérifier ({toVerify.length}){stuck.length > 0 ? ` · ${stuck.length} bloquée${stuck.length > 1 ? "s" : ""} + 24 h` : ""}</h3>
          <p className="text-zinc-400 text-xs mb-3">Après 72 h en « Acceptée » / « En livraison » sans clôture, la commande passe « À vérifier » (jamais clôturée automatiquement). Tranche : Livrée ou Annulée. Le vendeur peut encore la clôturer lui-même.</p>
          <div className="space-y-2">
            {[...toVerify, ...stuck].map((o) => (
              <div key={o.id} data-testid="admin-verify-row" className="flex flex-wrap items-center gap-2 border border-zinc-800 bg-ink-surface px-3 py-2 text-sm">
                <span className="font-mono text-neon-green">#{o.ref}</span>
                <span className={`text-[10px] uppercase font-bold border px-2 py-0.5 ${o.status === "a_verifier" ? "border-orange-400 text-orange-400" : "border-zinc-600 text-zinc-300"}`}>{STATUS_LABEL[o.status] || o.status}{o.stuck_hours ? ` · ${Math.floor(o.stuck_hours)} h` : ""}</span>
                <span className="text-zinc-300">{o.vendor_name} · {o.city}</span>
                <span className="text-zinc-500 text-xs">{o.customer_name} · {o.customer_phone}</span>
                <span className="font-anton text-neon-green ml-auto">{Number(o.total).toFixed(2)}€</span>
                <a href={whatsappLink(o.customer_phone, `Bonjour ${o.customer_name}, STUFF CBD : as-tu bien reçu ta commande #${o.ref} ?`)} target="_blank" rel="noopener noreferrer" data-testid="admin-verify-whatsapp" className="min-h-[36px] inline-flex items-center gap-1 text-xs font-bold uppercase border border-zinc-700 text-zinc-300 px-2 hover:border-neon-green"><MessageCircle className="h-3 w-3" /> Client</a>
                <button data-testid="admin-verify-delivered" onClick={() => decide(o, "livree")} className="min-h-[36px] text-xs font-bold uppercase bg-neon-green text-black px-3">✓ Livrée</button>
                <button data-testid="admin-verify-cancel" onClick={() => decide(o, "annulee")} className="min-h-[36px] text-xs font-bold uppercase border border-red-500 text-red-400 px-3 hover:bg-red-500 hover:text-white">✕ Annulée</button>
              </div>
            ))}
          </div>
        </div>
      )}
      <NationalPaymentSettings />
      <AdminListFilters rows={orders} filters={filters} setFilters={setFilters} shown={shown.length} total={orders.length} testPrefix="orders-filter" />
      {(filters.from || filters.to || filters.vendor) && shown.length > 0 && <p data-testid="orders-filter-sum" className="text-zinc-400 text-xs mb-3">Sélection : <span className="text-neon-green font-bold">{shownTotal.toFixed(2)}€</span> sur {shown.length} commande{shown.length > 1 ? "s" : ""}</p>}
      {shown.length === 0 && <p data-testid="orders-filter-empty" className="text-zinc-500 py-6 text-center">Aucune commande pour ces filtres.</p>}
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-zinc-500 uppercase text-xs border-b border-zinc-800">
          <th className="text-left py-2 px-2">Réf</th><th className="text-left px-2">Date</th><th className="text-left px-2">Client</th><th className="text-left px-2">Ville</th><th className="text-left px-2">Vendeur</th><th className="text-right px-2">Total</th><th className="text-left px-2">Statut</th><th className="text-right px-2">Reçu</th>
        </tr></thead>
        <tbody>
          {shown.map((o) => (
            <tr key={o.id} data-testid="admin-order-row" className="border-b border-zinc-900 text-zinc-300">
              <td className="py-2 px-2 font-mono text-neon-green">#{o.ref}{o.invoice_number && <><br /><span data-testid="admin-order-invoice-number" className="text-neon-magenta text-xs">{o.invoice_number}</span></>}</td>
              <td className="px-2 whitespace-nowrap text-xs" data-testid="admin-order-date">{new Date(o.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</td>
              <td className="px-2">{o.customer_name}<br /><span className="text-zinc-600 text-xs">{o.customer_phone}</span></td>
              <td className="px-2">{o.city}</td>
              <td className="px-2">{o.vendor_name}</td>
              <td className="px-2 text-right font-anton text-neon-green">{Number(o.total).toFixed(2)}€</td>
              <td className="px-2 text-xs uppercase">{STATUS_LABEL[o.status] || o.status}<br /><span data-testid="admin-order-payment" className="text-zinc-500 normal-case">{payLabel(o.payment_method)}{o.paid_at ? " ✓" : ""}</span>
                {o.status === "attente_paiement" && hoursSince(o.created_at) >= 24 && <><br /><span data-testid="admin-order-remind" className="text-yellow-400 normal-case">⏰ à relancer</span></>}
              </td>
              <td className="px-2 text-right">
                {o.status === "attente_paiement" && (
                  <>
                    <button data-testid="admin-payment-received" onClick={() => paymentReceived(o)} className="inline-flex items-center gap-1 text-xs font-bold uppercase bg-neon-green text-black px-2 py-1 mr-1">✓ Paiement reçu</button>
                    <a data-testid="admin-remind-whatsapp" href={whatsappLink(o.customer_phone, `Bonjour ${o.customer_name}, STUFF France : ta commande #${o.ref} (${Number(o.total).toFixed(2)}€) est en attente de ton virement instantané, libellé ${o.payment_ref || `STUFF-${o.ref}`}. Elle sera annulée automatiquement sans paiement. Un souci ? Réponds-nous ici 🙂`)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-bold uppercase border border-zinc-700 text-zinc-300 px-2 py-1 mr-1 hover:border-neon-green"><MessageCircle className="h-3 w-3" /> Relancer</a>
                  </>
                )}
                <button data-testid="admin-invoice-btn" onClick={() => invoice(o)} title="Télécharger le reçu PDF" className="inline-flex items-center gap-1 text-xs text-zinc-300 border border-zinc-700 px-2 py-1 hover:border-neon-magenta hover:text-neon-magenta transition-colors"><FileText className="h-3 w-3" /> PDF</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </div>
  );
}

function CustomersTab() {
  const [customers, setCustomers] = useState([]);
  useEffect(() => { api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(console.error); }, []);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/customers/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "clients_stuff.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
    } catch { toast.error("Erreur export"); }
  };

  return (
    <div>
      <button data-testid="admin-export-csv" onClick={exportCsv} className="flex items-center gap-2 bg-neon-magenta text-white font-anton uppercase px-5 py-3 glow-magenta mb-6"><Download className="h-5 w-5" /> Exporter le fichier clients (CSV)</button>
      {customers.length === 0 ? <p className="text-zinc-500 py-10 text-center">Aucun client.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-zinc-500 uppercase text-xs border-b border-zinc-800">
              <th className="text-left py-2 px-2">Client</th><th className="text-left px-2">Téléphone</th><th className="text-left px-2">Ville</th><th className="text-right px-2">Cmd</th><th className="text-right px-2">Total dépensé</th>
            </tr></thead>
            <tbody>
              {customers.map((c, i) => (
                <tr key={c.phone || i} data-testid="admin-customer-row" className="border-b border-zinc-900 text-zinc-300">
                  <td className="py-2 px-2">{c.customer_name}</td>
                  <td className="px-2">{c.customer_phone}</td>
                  <td className="px-2">{c.city}</td>
                  <td className="px-2 text-right">{c.order_count}</td>
                  <td className="px-2 text-right font-anton text-neon-green">{Number(c.total_spent).toFixed(2)}€</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ApplicationsTab() {
  const [apps, setApps] = useState([]);
  const load = () => api.get("/admin/applications").then((r) => setApps(r.data)).catch(console.error);
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { await api.patch(`/admin/applications/${id}`, { status }); load(); };
  const openCv = (a) => downloadFile(`/admin/applications/${a.id}/cv`, a.cv_name || `cv-${a.name}.pdf`).catch(() => toast.error("CV indisponible"));
  const exportCsv = () => downloadFile("/admin/applications/export.csv", `candidatures-livreurs-${new Date().toISOString().slice(0, 10)}.csv`).catch(() => toast.error("Erreur export"));
  const LICENCE = { AM: "Permis AM", B: "Permis B", aucun: "Sans permis" };

  return (
    <div data-testid="admin-applications">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="font-anton text-2xl uppercase text-white">Candidatures livreurs <span className="text-neon-magenta">({apps.length})</span></h2>
          <p className="text-zinc-500 text-sm">Reçues depuis les offres d'emploi du blog (sans compte vendeur).</p>
        </div>
        <button data-testid="applications-export-csv" onClick={exportCsv} className="inline-flex items-center gap-2 text-xs uppercase font-bold border border-zinc-700 text-zinc-300 px-3 py-2 hover:border-neon-green hover:text-neon-green"><Download className="h-4 w-4" /> Export CSV</button>
      </div>
      {apps.length === 0 && <p className="text-zinc-500 py-10 text-center">Aucune candidature.</p>}
      <div className="space-y-3">
        {apps.map((a) => (
          <div key={a.id} data-testid="admin-application-row" className={`bg-ink-surface border p-4 flex flex-col sm:flex-row sm:items-start gap-3 ${a.status === "nouvelle" ? "border-neon-magenta/60" : "border-zinc-800"}`}>
            <div className="flex-1 min-w-0">
              <span className="font-anton text-lg text-white">{a.name}</span>
              <span className={`ml-3 text-xs uppercase font-bold ${a.status === "nouvelle" ? "text-neon-magenta" : a.status === "refusé" ? "text-red-400" : "text-zinc-500"}`}>{a.status}</span>
              <p className="text-zinc-400 text-sm">📞 {a.phone} {a.email && `· ✉️ ${a.email}`} — 📍 {a.city}</p>
              <p className="text-zinc-500 text-xs mt-1">{LICENCE[a.licence] || a.licence || "—"}{a.vehicle && ` · 🛵 ${a.vehicle}`}{a.availability && ` · 🕒 ${a.availability}`}</p>
              {a.message ? <p className="text-zinc-500 text-xs mt-1 italic">{a.message}</p> : null}
              <p className="text-zinc-600 text-[11px] mt-1">{a.created_at ? new Date(a.created_at).toLocaleString("fr-FR") : ""}{a.source_slug && ` · via /blog/${a.source_slug}`}</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {a.cv_path && <button data-testid="app-cv" onClick={() => openCv(a)} className="inline-flex items-center gap-1 text-xs uppercase font-bold border border-neon-green text-neon-green px-3 py-2 hover:bg-neon-green hover:text-black"><FileText className="h-3.5 w-3.5" /> CV</button>}
              <a data-testid="app-whatsapp" href={whatsappLink(a.phone, `Bonjour ${a.name}, STUFF — suite à ta candidature livreur à ${a.city} :`)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs uppercase font-bold px-3 py-2 bg-neon-green text-black"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>
              <button data-testid="app-contacted" onClick={() => setStatus(a.id, "contacté")} className="text-xs uppercase font-bold border border-zinc-700 text-zinc-300 px-3 py-2 hover:border-neon-green hover:text-neon-green">Contacté</button>
              <button data-testid="app-rejected" onClick={() => setStatus(a.id, "refusé")} className="text-xs uppercase font-bold border border-zinc-700 text-zinc-300 px-3 py-2 hover:border-red-500 hover:text-red-400">Refuser</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


const EMPTY_GIFT = { name: "", points_cost: "", description: "", image_url: "", active: true };

function GiftsTab() {
  const [gifts, setGifts] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/gifts").then((r) => setGifts(r.data)).catch(console.error);
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...editing, points_cost: parseInt(editing.points_cost) || 0 };
    try {
      if (editing.id) await api.put(`/admin/gifts/${editing.id}`, payload);
      else await api.post("/admin/gifts", payload);
      setEditing(null); load(); toast.success("Cadeau enregistré");
    } catch { toast.error("Erreur"); }
  };

  const del = async (id) => { if (!window.confirm("Supprimer ce cadeau ?")) return; await api.delete(`/admin/gifts/${id}`); load(); toast.success("Supprimé"); };

  return (
    <div>
      {!editing && (
        <button data-testid="add-gift-btn" onClick={() => setEditing({ ...EMPTY_GIFT })} className="flex items-center gap-2 bg-neon-magenta text-white font-anton uppercase px-5 py-3 glow-magenta mb-6"><Plus className="h-5 w-5" /> Ajouter un cadeau</button>
      )}
      {editing && (
        <form onSubmit={save} className="bg-ink-surface border border-neon-magenta/40 p-6 mb-6 space-y-3" data-testid="gift-form">
          <div className="flex justify-between items-center"><h3 className="font-anton text-xl uppercase text-white">{editing.id ? "Modifier" : "Nouveau cadeau"}</h3><button type="button" onClick={() => setEditing(null)}><X className="h-5 w-5 text-zinc-400" /></button></div>
          <input data-testid="gift-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Nom du cadeau" className="w-full bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
          <input data-testid="gift-cost" type="number" value={editing.points_cost} onChange={(e) => setEditing({ ...editing, points_cost: e.target.value })} placeholder="Coût en points" className="w-full bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
          <textarea data-testid="gift-desc" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={2} placeholder="Description" className="w-full bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
          <label className="flex items-center gap-2 text-zinc-300 text-sm"><input type="checkbox" data-testid="gift-active" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="accent-[#c026d3]" /> Actif (visible des clients)</label>
          <button type="submit" data-testid="gift-save" className="flex items-center gap-2 bg-neon-magenta text-white font-anton uppercase px-5 py-2 glow-magenta"><Save className="h-4 w-4" /> Enregistrer</button>
        </form>
      )}
      {gifts.length === 0 ? <p className="text-zinc-500 py-10 text-center">Aucun cadeau. Crée le premier !</p> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {gifts.map((g) => (
            <div key={g.id} data-testid="admin-gift-row" className="flex items-center gap-3 bg-ink-surface border border-zinc-800 p-4">
              <div className="flex-1">
                <div className="flex items-center gap-2"><span className="font-anton text-lg text-white">{g.name}</span>{!g.active && <span className="text-xs text-zinc-500 border border-zinc-700 px-2">inactif</span>}</div>
                <span className="flex items-center gap-1 text-neon-magenta font-bold text-sm"><Star className="h-4 w-4 fill-neon-magenta" /> {g.points_cost} pts</span>
                {g.description ? <p className="text-zinc-500 text-xs mt-1">{g.description}</p> : null}
              </div>
              <button data-testid="edit-gift" onClick={() => setEditing({ ...g, points_cost: String(g.points_cost) })} className="p-2 text-zinc-400 hover:text-neon-magenta"><Pencil className="h-4 w-4" /></button>
              <button data-testid="delete-gift" onClick={() => del(g.id)} className="p-2 text-zinc-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RedemptionsTab() {
  const [reds, setReds] = useState([]);
  const load = () => api.get("/admin/redemptions").then((r) => setReds(r.data)).catch(console.error);
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { await api.patch(`/admin/redemptions/${id}`, { status }); load(); toast.success("Mis à jour"); };

  if (reds.length === 0) return <p className="text-zinc-500 py-10 text-center">Aucun échange de cadeau.</p>;
  return (
    <div className="space-y-3">
      {reds.map((r) => (
        <div key={r.id} data-testid="admin-redemption-row" className="bg-ink-surface border border-zinc-800 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <span className="font-mono text-neon-green">#{r.ref}</span>
            <span className="ml-3 font-anton text-lg text-white">{r.gift_name}</span>
            <span className="ml-2 text-xs uppercase text-zinc-500">{r.status}</span>
            <p className="text-zinc-400 text-sm">📞 {r.phone} — {r.points_cost} pts</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="redemption-given" onClick={() => setStatus(r.id, "remis")} className="text-xs uppercase font-bold border border-zinc-700 text-zinc-300 px-3 py-2 hover:border-neon-green hover:text-neon-green">Marquer remis</button>
          </div>
        </div>
      ))}
    </div>
  );
}

const parsePrice = (v) => { const n = parseFloat(String(v ?? "").replace(",", ".")); return isNaN(n) ? null : n; };
const autoMargins = (priceStr) => {
  const p = parsePrice(priceStr);
  if (!p || p <= 0) return {};
  const ht = p / 1.2;
  return { factory_price: (ht * 0.5).toFixed(2) };
};

function CategoriesManager({ categories, onChanged }) {
  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [newCat, setNewCat] = useState({ name: "", note: "", active: true });

  const d = (c) => drafts[c.id] || { name: c.name, note: c.note || "", active: c.active !== false };
  const setD = (id, patch) => setDrafts((p) => ({ ...p, [id]: { ...(p[id] || {}), ...patch } }));

  const saveCat = async (c) => {
    try {
      await api.put(`/admin/categories/${c.id}`, { ...d(c) });
      toast.success("Catégorie mise à jour");
      setDrafts((p) => { const n = { ...p }; delete n[c.id]; return n; });
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const delCat = async (c) => {
    if (!window.confirm(`Supprimer la catégorie "${c.name}" ?`)) return;
    try { await api.delete(`/admin/categories/${c.id}`); toast.success("Catégorie supprimée"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const addCat = async () => {
    if (!newCat.name.trim()) return;
    try {
      await api.post("/admin/categories", newCat);
      toast.success("Catégorie créée");
      setNewCat({ name: "", note: "", active: true });
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="border border-zinc-800 bg-ink-surface mb-6" data-testid="categories-manager">
      <button type="button" data-testid="categories-toggle" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 text-left">
        <span className="font-anton uppercase text-white">Catégories produit <span className="text-zinc-500 text-xs font-sans normal-case">({categories.length})</span></span>
        <span className="text-neon-magenta text-xs uppercase font-bold">{open ? "Fermer ▲" : "Gérer ▼"}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2">
          {categories.map((c) => {
            const dc = d(c);
            const dirty = dc.name !== c.name || dc.note !== (c.note || "") || dc.active !== (c.active !== false);
            return (
              <div key={c.id} data-testid="category-row" className={`flex flex-wrap items-center gap-2 border p-2 ${dc.active ? "border-zinc-800" : "border-zinc-800 opacity-50"}`}>
                <input data-testid="category-name-input" value={dc.name} onChange={(e) => setD(c.id, { name: e.target.value })} className="flex-1 min-w-[140px] bg-ink border border-zinc-700 text-white px-2 py-1.5 text-sm focus:outline-none focus:border-neon-magenta" />
                <input data-testid="category-note-input" value={dc.note} onChange={(e) => setD(c.id, { note: e.target.value })} placeholder="Note (ex: Ouverture en octobre)" className="flex-1 min-w-[160px] bg-ink border border-zinc-700 text-zinc-300 px-2 py-1.5 text-xs focus:outline-none focus:border-neon-magenta" />
                <label className="flex items-center gap-1.5 text-xs cursor-pointer" title={dc.active ? "Catégorie active (visible clients)" : "Catégorie grisée (masquée clients)"}>
                  <input type="checkbox" data-testid="category-active-toggle" checked={dc.active} onChange={(e) => setD(c.id, { active: e.target.checked })} className="accent-[#9dff00]" />
                  <span className={dc.active ? "text-neon-green font-bold uppercase" : "text-zinc-500 uppercase"}>{dc.active ? "Active" : "Grisée"}</span>
                </label>
                {!dc.active && dc.note && <span className="text-[10px] text-zinc-500 italic">({dc.note})</span>}
                <button data-testid="category-save" onClick={() => saveCat(c)} disabled={!dirty} className={`p-1.5 ${dirty ? "text-neon-green" : "text-zinc-700"}`} title="Enregistrer"><Save className="h-4 w-4" /></button>
                <button data-testid="category-delete" onClick={() => delCat(c)} className="p-1.5 text-zinc-500 hover:text-red-500" title="Supprimer"><Trash2 className="h-4 w-4" /></button>
              </div>
            );
          })}
          <div className="flex flex-wrap items-center gap-2 border border-dashed border-zinc-700 p-2">
            <input data-testid="category-new-name" value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} placeholder="Nouvelle catégorie" className="flex-1 min-w-[140px] bg-ink border border-zinc-700 text-white px-2 py-1.5 text-sm focus:outline-none focus:border-neon-magenta" />
            <input data-testid="category-new-note" value={newCat.note} onChange={(e) => setNewCat({ ...newCat, note: e.target.value })} placeholder="Note (optionnel)" className="flex-1 min-w-[140px] bg-ink border border-zinc-700 text-zinc-300 px-2 py-1.5 text-xs focus:outline-none focus:border-neon-magenta" />
            <label className="flex items-center gap-1.5 text-xs text-zinc-300 cursor-pointer">
              <input type="checkbox" checked={newCat.active} onChange={(e) => setNewCat({ ...newCat, active: e.target.checked })} className="accent-[#9dff00]" /> Active
            </label>
            <button data-testid="category-add" onClick={addCat} className="flex items-center gap-1 bg-neon-magenta text-white text-xs font-bold uppercase px-3 py-1.5"><Plus className="h-3 w-3" /> Ajouter</button>
          </div>
          <p className="text-zinc-600 text-[11px]">Une catégorie « grisée » est masquée des boutiques clients (les produits restent dans le catalogue). Renommer une catégorie met à jour tous ses produits.</p>
        </div>
      )}
    </div>
  );
}

const EMPTY_CATALOG = { name: "", description: "", long_description: "", cbd_rate: "", thc_rate: "", usage_tips: "", culture_type: "", origin: "", effects: null, tiers: [], gallery: [], terpenes: [], terpenes_total: "", price: "", category: "Fleurs CBD", image_url: "", commission_pct: 70, active: true, factory_stock: 0, factory_price: "", lab_url: "", promo_active: false, promo_pct: "", promo_start: "", promo_end: "" };

function ReviewsModeration() {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/product-reviews").then((r) => setRows(r.data)).catch(console.error);
  useEffect(() => { if (open) load(); }, [open]);
  const del = async (id) => {
    if (!window.confirm("Supprimer cet avis ?")) return;
    try { await api.delete(`/admin/product-reviews/${id}`); load(); toast.success("Avis supprimé"); }
    catch { toast.error("Erreur"); }
  };
  return (
    <div className="border border-zinc-800 bg-ink-surface mb-6" data-testid="reviews-moderation">
      <button type="button" data-testid="reviews-moderation-toggle" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 text-left">
        <span className="font-anton uppercase text-white text-sm">⭐ Avis produits (modération)</span>
        <span className="text-zinc-500 text-xs">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2 max-h-80 overflow-y-auto">
          {rows.length === 0 && <p className="text-zinc-500 text-sm">Aucun avis produit pour l'instant.</p>}
          {rows.map((r) => (
            <div key={r.id} className="flex items-start gap-3 border border-zinc-800 bg-ink p-3" data-testid="moderation-review-item">
              {r.photo_url && <img src={imageUrl(r.photo_url)} alt="" className="h-12 w-12 object-cover border border-zinc-700 shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-semibold truncate">{r.product_name} — {"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</p>
                <p className="text-zinc-400 text-xs">{r.author} · {r.phone} · {new Date(r.created_at).toLocaleDateString()}</p>
                {r.text && <p className="text-zinc-500 text-xs mt-1 line-clamp-2">{r.text}</p>}
              </div>
              <button data-testid="moderation-review-delete" onClick={() => del(r.id)} className="text-red-400 hover:text-red-300 text-xs uppercase font-bold shrink-0">Suppr.</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CatalogTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState([]);

  const load = () => api.get("/admin/catalog").then((r) => setItems(r.data)).catch(console.error);
  const loadCats = () => api.get("/categories").then((r) => setCategories(Array.isArray(r.data) ? r.data : [])).catch(console.error);
  useEffect(() => { load(); loadCats(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...editing, price: parsePrice(editing.price) || 0, commission_pct: parseFloat(editing.commission_pct) || 0, factory_stock: parseInt(editing.factory_stock) || 0, factory_price: parsePrice(editing.factory_price) || 0,
      promo_active: !!editing.promo_active, promo_pct: Math.min(90, Math.max(0, parseFloat(editing.promo_pct) || 0)), promo_start: editing.promo_start || null, promo_end: editing.promo_end || null,
      tiers: (editing.tiers || []).filter((x) => String(x.label || "").trim() && String(x.price ?? "") !== "").map((x) => ({ label: String(x.label).trim(), price: parsePrice(x.price) || 0, units: parseInt(x.units) || 1 })),
      gallery: (editing.gallery || []).filter(Boolean).slice(0, 4),
      terpenes: (editing.terpenes || []).filter((x) => String(x.name || "").trim() && String(x.pct ?? "") !== "").map((x) => ({ name: String(x.name).trim(), pct: parseFloat(String(x.pct).replace(",", ".")) || 0, note: String(x.note || "").trim() })),
      terpenes_total: String(editing.terpenes_total || "").trim(),
      effects: editing.effects ? Object.fromEntries(Object.entries(editing.effects).filter(([, v]) => v !== "" && v != null).map(([k, v]) => [k, Math.max(0, Math.min(10, parseInt(v) || 0))])) : null };
    try {
      if (editing.id) await api.put(`/admin/catalog/${editing.id}`, payload);
      else await api.post("/admin/catalog", payload);
      setEditing(null); load(); toast.success("Produit du catalogue enregistré");
    } catch { toast.error("Erreur"); }
  };

  const del = async (id) => { if (!window.confirm("Supprimer ce produit du catalogue ? (retiré chez tous les vendeurs)")) return; await api.delete(`/admin/catalog/${id}`); load(); toast.success("Supprimé"); };

  const [aiLoading, setAiLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const generateAI = async () => {
    setAiLoading(true);
    try {
      const r = await api.post(`/admin/catalog/${editing.id}/generate-description`);
      setEditing((p) => ({ ...p, ...r.data }));
      toast.success("Description générée — relis et enregistre");
    } catch { toast.error("Génération IA indisponible, réessaie"); }
    setAiLoading(false);
  };
  const generateBulk = async () => {
    if (!window.confirm("Générer par IA les descriptions de tous les produits qui n'en ont pas ?")) return;
    setBulkLoading(true);
    try {
      const r = await api.post("/admin/catalog/generate-descriptions");
      load();
      toast.success(`${r.data.generated} description(s) générée(s)${r.data.errors ? `, ${r.data.errors} erreur(s)` : ""}`);
    } catch { toast.error("Erreur de génération"); }
    setBulkLoading(false);
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((p) => ({ ...p, image_url: data.url }));
      toast.success("Image ajoutée");
    } catch { toast.error("Échec de l'upload"); }
    finally { setUploading(false); }
  };

  const uploadLab = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((p) => ({ ...p, lab_url: data.url }));
      toast.success("Certificat d'analyse ajouté");
    } catch { toast.error("Échec de l'upload"); }
    finally { setUploading(false); }
  };

  return (
    <div>
      <div className="border border-zinc-800 bg-ink-surface p-4 mb-6 text-sm text-zinc-400">
        <Package className="h-4 w-4 text-neon-magenta inline mr-2" />
        Catalogue central de la <span className="text-neon-magenta font-semibold">maison mère</span>. Les produits, prix et <span className="text-white font-semibold">% reversé au vendeur</span> sont définis ici. Les vendeurs gèrent seulement leurs quantités.
      </div>

      <CategoriesManager categories={categories} onChanged={() => { loadCats(); load(); }} />

      <ReviewsModeration />

      {!editing && (
        <button data-testid="add-catalog-btn" onClick={() => setEditing({ ...EMPTY_CATALOG })} className="flex items-center gap-2 bg-neon-magenta text-white font-anton uppercase px-5 py-3 glow-magenta mb-6">
          <Plus className="h-5 w-5" /> Ajouter un produit
        </button>
      )}
      {!editing && (
        <button data-testid="catalog-bulk-ai-btn" onClick={generateBulk} disabled={bulkLoading} className="mb-6 ml-3 inline-flex items-center gap-2 border border-neon-magenta text-neon-magenta font-anton uppercase px-6 py-3 hover:bg-neon-magenta hover:text-white transition-colors disabled:opacity-40">
          {bulkLoading ? "Génération en cours…" : "✨ Générer descriptions manquantes (IA)"}
        </button>
      )}

      {editing && (
        <form onSubmit={save} className="bg-ink-surface border border-neon-magenta/40 p-6 mb-6 space-y-4" data-testid="catalog-form">
          <div className="flex justify-between items-center">
            <h3 className="font-anton text-xl uppercase text-white">{editing.id ? "Modifier" : "Nouveau produit"}</h3>
            <button type="button" onClick={() => setEditing(null)}><X className="h-5 w-5 text-zinc-400 hover:text-white" /></button>
          </div>
          <div className="flex gap-4 items-start">
            <label className="h-24 w-24 shrink-0 border border-zinc-700 flex items-center justify-center cursor-pointer overflow-hidden bg-ink hover:border-neon-magenta" data-testid="catalog-image-label">
              {editing.image_url ? <img src={imageUrl(editing.image_url)} alt="" className="h-full w-full object-cover" /> : <Upload className="h-6 w-6 text-zinc-500" />}
              <input type="file" accept="image/*" className="hidden" data-testid="catalog-image-input" onChange={(e) => upload(e.target.files[0])} />
            </label>
            <div className="flex-1 space-y-3">
              <input data-testid="catalog-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Nom du produit" className="w-full bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
              <div className="flex gap-3">
                <input data-testid="catalog-price" type="text" inputMode="decimal" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value, ...(editing.id ? {} : autoMargins(e.target.value)) })} placeholder="Prix conseillé TTC € (ex: 9.90)" className="w-1/2 bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
                <select data-testid="catalog-category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="w-1/2 bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta">
                  {(categories.length ? categories.map((c) => c.name) : [editing.category]).map((c) => <option key={c} value={c}>{c}</option>)}
                  {categories.length > 0 && !categories.some((c) => c.name === editing.category) && <option value={editing.category}>{editing.category}</option>}
                </select>
              </div>
            </div>
          </div>
          <textarea data-testid="catalog-desc" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={2} placeholder="Description courte (carte produit)" className="w-full bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-zinc-400 text-xs uppercase">Description longue (fiche produit)</span>
              {editing.id && (
                <button type="button" data-testid="catalog-ai-btn" disabled={aiLoading} onClick={generateAI} className="text-xs uppercase font-bold border border-neon-magenta text-neon-magenta px-3 py-1 hover:bg-neon-magenta hover:text-white transition-colors disabled:opacity-40">
                  {aiLoading ? "Génération…" : "✨ Générer par IA"}
                </button>
              )}
            </div>
            <textarea data-testid="catalog-long-desc" value={editing.long_description || ""} onChange={(e) => setEditing({ ...editing, long_description: e.target.value })} rows={4} placeholder="Description détaillée : arômes, profil, moment idéal… (générable par IA)" className="w-full bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
          </div>
          <textarea data-testid="catalog-tips" value={editing.usage_tips || ""} onChange={(e) => setEditing({ ...editing, usage_tips: e.target.value })} rows={2} placeholder="Conseils d'utilisation / conservation" className="w-full bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
          <div className="flex gap-3">
            <input data-testid="catalog-cbd-rate" value={editing.cbd_rate || ""} onChange={(e) => setEditing({ ...editing, cbd_rate: e.target.value })} placeholder="Taux CBD (ex: 12%)" className="w-1/2 bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
            <input data-testid="catalog-thc-rate" value={editing.thc_rate || ""} onChange={(e) => setEditing({ ...editing, thc_rate: e.target.value })} placeholder="Taux THC (ex: < 0,3%)" className="w-1/2 bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
          </div>
          <div className="flex gap-3">
            <input data-testid="catalog-culture" value={editing.culture_type || ""} onChange={(e) => setEditing({ ...editing, culture_type: e.target.value })} placeholder="Type de culture (ex: Indoor)" className="w-1/2 bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
            <input data-testid="catalog-origin" value={editing.origin || ""} onChange={(e) => setEditing({ ...editing, origin: e.target.value })} placeholder="Origine (ex: Suisse)" className="w-1/2 bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
          </div>
          <label className="block text-zinc-400 text-xs uppercase">Effets ressentis (0–10 — barres sur la fiche produit)
            <div className="flex gap-4 mt-1">
              {[["relax", "Relaxation"], ["energy", "Énergie"], ["sleep", "Sommeil"]].map(([k, lab]) => (
                <div key={k}>
                  <span className="text-zinc-500 text-xs normal-case block">{lab}</span>
                  <input data-testid={`catalog-effect-${k}`} type="number" min="0" max="10" value={editing.effects?.[k] ?? ""} onChange={(e) => setEditing({ ...editing, effects: { ...(editing.effects || {}), [k]: e.target.value } })} className="w-20 bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
                </div>
              ))}
            </div>
          </label>
          <label className="block text-zinc-400 text-xs uppercase">Galerie photos (max 4, en plus de l'image principale)
            <div className="flex flex-wrap gap-2 mt-1 items-center">
              {(editing.gallery || []).map((g, gi) => (
                <div key={g} className="relative">
                  <img src={imageUrl(g)} alt="" className="h-16 w-16 object-cover border border-zinc-700" />
                  <button type="button" data-testid="gallery-remove" onClick={() => setEditing({ ...editing, gallery: editing.gallery.filter((_, j) => j !== gi) })} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full h-5 w-5 text-xs leading-none">✕</button>
                </div>
              ))}
              {(editing.gallery || []).length < 4 && (
                <label className="h-16 w-16 border border-dashed border-zinc-600 flex items-center justify-center text-zinc-500 cursor-pointer hover:border-neon-magenta hover:text-neon-magenta text-2xl">
                  +
                  <input type="file" accept="image/*" className="hidden" data-testid="gallery-upload-input" onChange={async (e) => {
                    const f = e.target.files[0];
                    if (!f) return;
                    const fd = new FormData();
                    fd.append("file", f);
                    try {
                      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                      setEditing((prev) => ({ ...prev, gallery: [...(prev.gallery || []), data.url] }));
                    } catch { toast.error("Échec de l'upload"); }
                  }} />
                </label>
              )}
            </div>
          </label>
          <label className="block text-zinc-400 text-xs uppercase">Profil terpénique (donut sur la fiche produit)
            <div className="flex gap-2 items-center mt-1 mb-2">
              <span className="text-zinc-500 text-xs normal-case">Terpènes totaux :</span>
              <input data-testid="terpenes-total-input" value={editing.terpenes_total || ""} onChange={(e) => setEditing({ ...editing, terpenes_total: e.target.value })} placeholder="ex: 3,4%" className="w-24 bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
            </div>
            <div className="space-y-2">
              {(editing.terpenes || []).map((x, xi) => (
                <div key={xi} className="flex gap-2 items-center flex-wrap">
                  <input data-testid="terpene-name-input" value={x.name} onChange={(e) => setEditing({ ...editing, terpenes: editing.terpenes.map((y, j) => j === xi ? { ...y, name: e.target.value } : y) })} placeholder="Nom (ex: Myrcène)" className="w-36 bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
                  <input data-testid="terpene-pct-input" inputMode="decimal" value={x.pct} onChange={(e) => setEditing({ ...editing, terpenes: editing.terpenes.map((y, j) => j === xi ? { ...y, pct: e.target.value } : y) })} placeholder="Part %" className="w-20 bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
                  <input data-testid="terpene-note-input" value={x.note || ""} onChange={(e) => setEditing({ ...editing, terpenes: editing.terpenes.map((y, j) => j === xi ? { ...y, note: e.target.value } : y) })} placeholder="Note (ex: agrumes acidulés)" className="flex-1 min-w-32 bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
                  <button type="button" data-testid="terpene-remove" onClick={() => setEditing({ ...editing, terpenes: editing.terpenes.filter((_, j) => j !== xi) })} className="text-red-400 hover:text-red-300 px-2">✕</button>
                </div>
              ))}
              <button type="button" data-testid="terpene-add" onClick={() => setEditing({ ...editing, terpenes: [...(editing.terpenes || []), { name: "", pct: "", note: "" }] })} className="border border-neon-magenta/60 text-neon-magenta text-xs font-bold uppercase px-3 py-2 hover:bg-neon-magenta hover:text-white transition-colors">+ Ajouter un terpène</button>
            </div>
          </label>
          <label className="block text-zinc-400 text-xs uppercase">Formats & tarifs dégressifs (optionnel)
            <div className="space-y-2 mt-1">
              {(editing.tiers || []).map((x, idx) => (
                <div key={idx} className="flex gap-2 items-center flex-wrap">
                  <input data-testid="tier-label-input" value={x.label} onChange={(e) => setEditing({ ...editing, tiers: editing.tiers.map((y, j) => j === idx ? { ...y, label: e.target.value } : y) })} placeholder="Format (ex: 5g)" className="w-28 bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
                  <input data-testid="tier-price-input" inputMode="decimal" value={x.price} onChange={(e) => setEditing({ ...editing, tiers: editing.tiers.map((y, j) => j === idx ? { ...y, price: e.target.value } : y) })} placeholder="Prix €" className="w-24 bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
                  <input data-testid="tier-units-input" type="number" min="1" value={x.units} onChange={(e) => setEditing({ ...editing, tiers: editing.tiers.map((y, j) => j === idx ? { ...y, units: e.target.value } : y) })} placeholder="Unités" className="w-24 bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
                  <button type="button" data-testid="tier-remove" onClick={() => setEditing({ ...editing, tiers: editing.tiers.filter((_, j) => j !== idx) })} className="text-red-400 hover:text-red-300 px-2">✕</button>
                </div>
              ))}
              <button type="button" data-testid="tier-add" onClick={() => setEditing({ ...editing, tiers: [...(editing.tiers || []), { label: "", price: "", units: 1 }] })} className="border border-neon-magenta/60 text-neon-magenta text-xs font-bold uppercase px-3 py-2 hover:bg-neon-magenta hover:text-white transition-colors">+ Ajouter un format</button>
            </div>
            <span className="text-zinc-500 text-xs normal-case block mt-1">« Unités » = unités de stock consommées par ce format (ex: format 5g = 5 si le stock est compté par gramme). Le prix du format remplace le prix de base à la commande.</span>
          </label>
          <div data-testid="catalog-promo-block" className={`border p-3 ${editing.promo_active ? "border-neon-magenta/70 bg-neon-magenta/5" : "border-zinc-800"}`}>
            <label className="flex items-center gap-2 text-xs uppercase text-zinc-300 cursor-pointer min-h-[44px]">
              <input data-testid="catalog-promo-active" type="checkbox" checked={!!editing.promo_active} onChange={(e) => setEditing({ ...editing, promo_active: e.target.checked })} className="accent-[#c026d3] h-4 w-4" />
              <span className={editing.promo_active ? "text-neon-magenta font-bold" : ""}>Promotion client (badge « PROMO -X % » + prix barré)</span>
            </label>
            {editing.promo_active && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
                <label className="text-zinc-400 text-xs uppercase">Rabais (%)
                  <div className="flex items-center gap-1 mt-1"><span className="text-neon-magenta font-anton">-</span>
                    <input data-testid="catalog-promo-pct" type="number" min="1" max="90" step="1" value={editing.promo_pct} onChange={(e) => setEditing({ ...editing, promo_pct: e.target.value })} className="w-full bg-ink border border-neon-magenta/50 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
                    <span className="text-zinc-400">%</span></div>
                </label>
                <label className="text-zinc-400 text-xs uppercase">Début
                  <input data-testid="catalog-promo-start" type="date" value={editing.promo_start || ""} onChange={(e) => setEditing({ ...editing, promo_start: e.target.value })} className="mt-1 w-full bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
                </label>
                <label className="text-zinc-400 text-xs uppercase">Fin (incluse)
                  <input data-testid="catalog-promo-end" type="date" value={editing.promo_end || ""} onChange={(e) => setEditing({ ...editing, promo_end: e.target.value })} className="mt-1 w-full bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
                </label>
                <p className="sm:col-span-3 text-zinc-500 text-xs normal-case">
                  {parsePrice(editing.price) > 0 && parseFloat(editing.promo_pct) > 0 ? <>Prix client : <span className="text-zinc-500 line-through">{parsePrice(editing.price).toFixed(2)}€</span> → <span className="text-neon-magenta font-bold">{(parsePrice(editing.price) * (1 - Math.min(90, parseFloat(editing.promo_pct) || 0) / 100)).toFixed(2)}€</span> · </> : null}
                  Sans dates = immédiat et sans fin. La remise est partagée 50/50 (le prix usine baisse d'autant). Si un vendeur a sa propre promo, la plus forte s'applique (non cumulées).
                </p>
              </div>
            )}
          </div>
          <label className="block text-zinc-400 text-xs uppercase">Prix usine → vendeur (coût) €
            <div className="flex flex-wrap items-center gap-3 mt-1">
              <div>
                <span className="text-neon-green text-xs normal-case block">Rémunération unique : vendeur 50 % du HT</span>
                <input data-testid="catalog-factory-price" type="text" inputMode="decimal" value={editing.factory_price} onChange={(e) => setEditing({ ...editing, factory_price: e.target.value })} placeholder="ex: 3.50" className="w-28 bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
              </div>
              <button type="button" data-testid="catalog-auto-margins" onClick={() => setEditing({ ...editing, ...autoMargins(editing.price) })} className="border border-neon-magenta/60 text-neon-magenta text-xs font-bold uppercase px-3 py-2 hover:bg-neon-magenta hover:text-white transition-colors">↻ Calcul auto (50 %)</button>
            </div>
            <span className="text-zinc-500 text-xs normal-case block mt-1">Auto-rempli à la création : HT = prix TTC ÷ 1,2 → prix usine = HT × 50 % (le vendeur garde l'autre moitié).</span>
          </label>
          <label className="block text-zinc-400 text-xs uppercase">Stock usine (maison mère)
            <input data-testid="catalog-factory-stock" type="number" min="0" value={editing.factory_stock} onChange={(e) => setEditing({ ...editing, factory_stock: e.target.value })} placeholder="0" className="mt-1 w-40 bg-ink border border-zinc-700 text-white px-3 py-2 focus:outline-none focus:border-neon-magenta" />
          </label>
          <label className="block text-zinc-400 text-xs uppercase">Analyse laboratoire (certificat PDF ou image)
            <div className="flex items-center gap-3 mt-1">
              <label className="cursor-pointer inline-flex items-center gap-2 border border-zinc-700 text-zinc-300 px-4 py-2 hover:border-neon-magenta transition-colors" data-testid="catalog-lab-label">
                <Upload className="h-4 w-4" /> {editing.lab_url ? "Remplacer" : "Ajouter le certificat"}
                <input type="file" accept="image/*,application/pdf" className="hidden" data-testid="catalog-lab-input" onChange={(e) => uploadLab(e.target.files[0])} />
              </label>
              {editing.lab_url && (
                <>
                  <a href={imageUrl(editing.lab_url)} target="_blank" rel="noopener noreferrer" className="text-neon-green text-xs underline normal-case" data-testid="catalog-lab-view">Voir le certificat</a>
                  <button type="button" data-testid="catalog-lab-remove" onClick={() => setEditing({ ...editing, lab_url: "" })} className="text-red-400 text-xs underline normal-case">Retirer</button>
                </>
              )}
            </div>
            <span className="text-zinc-500 text-xs normal-case block mt-1">Affiché sur la fiche produit : badge « 🔬 Analyse labo » cliquable — rassure les clients.</span>
          </label>
          <label className="flex items-center gap-2 text-zinc-300 text-sm">
            <input type="checkbox" data-testid="catalog-active" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="accent-[#c026d3]" /> Actif (visible des vendeurs et clients)
          </label>
          <button type="submit" data-testid="catalog-save" disabled={uploading} className="flex items-center gap-2 bg-neon-magenta text-white font-anton uppercase px-5 py-2 glow-magenta disabled:opacity-50">
            <Save className="h-4 w-4" /> {uploading ? "Upload..." : "Enregistrer"}
          </button>
        </form>
      )}

      {items.length === 0 ? (
        <p className="text-zinc-500 py-10 text-center">Catalogue vide. Crée le premier produit !</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {items.map((p) => (
            <div key={p.id} data-testid="admin-catalog-row" className="flex items-center gap-3 bg-ink-surface border border-zinc-800 p-3">
              <img src={imageUrl(p.image_url) || "https://customer-assets.emergentagent.com/job_store-hub-157/artifacts/yyc18991_IMG_4514.jpeg"} alt={p.name} className="h-14 w-14 object-cover" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><h4 className="text-white font-semibold truncate">{p.name}</h4>{!p.active && <span className="text-xs text-zinc-500 border border-zinc-700 px-2">inactif</span>}</div>
                <span className="text-neon-green font-anton">{Number(p.price).toFixed(2)}€</span>
                <span className="ml-2 text-xs text-zinc-500">{p.category}</span>
                <span className="ml-2 text-xs text-neon-magenta">Usine {Number(p.factory_price || 0).toFixed(2)}€ · vendeur 50 %</span>
                {p.promo_active && Number(p.promo_pct) > 0 && (
                  <span data-testid="catalog-promo-badge" className="ml-2 text-[10px] uppercase font-bold text-black bg-neon-magenta px-2 py-0.5 rounded-full">
                    PROMO -{Math.round(p.promo_pct)}%{p.promo_start || p.promo_end ? ` · ${p.promo_start ? `du ${new Date(p.promo_start).toLocaleDateString("fr-FR")}` : ""}${p.promo_end ? ` au ${new Date(p.promo_end).toLocaleDateString("fr-FR")}` : " sans fin"}` : ""}
                  </span>
                )}
              </div>
              <button data-testid="edit-catalog" onClick={() => setEditing({ ...p, price: String(p.price), factory_price: String(p.factory_price ?? ""), promo_pct: p.promo_pct ? String(p.promo_pct) : "", promo_start: p.promo_start || "", promo_end: p.promo_end || "" })} className="p-2 text-zinc-400 hover:text-neon-magenta"><Pencil className="h-4 w-4" /></button>
              <button data-testid="delete-catalog" onClick={() => del(p.id)} className="p-2 text-zinc-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

